import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppHeader from './AppHeader';
import {
  getCases,
  getCase,
  createCase,
  updateCase,
  addCaseComment,
  getCaseMeta,
  getUsers,
} from '../api';

const emptyForm = {
  subject: '',
  description: '',
  status: 'New',
  priority: 'Medium',
  origin: 'In Person',
  guestName: '',
  guestEmail: '',
  guestPhone: '',
  ticketType: '',
  ticketId: '',
  seasonPassId: '',
  ownerId: '',
};

function Cases({
  user,
  onLogout,
  onNavigate,
  initialCaseId = null,
  initialPrefill = null,
  returnToSeasonPassId = null,
}) {
  const [view, setView] = useState(initialCaseId ? 'detail' : initialPrefill ? 'new' : 'inbox');
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [meta, setMeta] = useState({
    statuses: ['New', 'Working', 'Waiting on Guest', 'Escalated', 'Closed'],
    priorities: ['Low', 'Medium', 'High', 'Urgent'],
    origins: ['In Person', 'Phone', 'Email', 'Web', 'Walk-up'],
  });
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('Open');
  const [query, setQuery] = useState('');
  const [form, setForm] = useState({
    ...emptyForm,
    ownerId: user?.id || '',
    ...(initialPrefill || {}),
  });
  const [commentBody, setCommentBody] = useState('');
  const [commentVisibility, setCommentVisibility] = useState('Public');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);


  const loadInbox = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { query };
      if (statusFilter === 'Open') {
        // client-side open filter after fetch all non-closed preferred
      } else if (statusFilter && statusFilter !== 'All') {
        params.status = statusFilter;
      }
      const data = await getCases(params);
      let list = data.cases || [];
      if (statusFilter === 'Open') {
        list = list.filter((entry) => entry.status !== 'Closed');
      }
      setCases(list);
    } catch (err) {
      setError(err.error || 'Unable to load cases');
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [query, statusFilter]);

  const openCase = useCallback(async (caseId) => {
    setError('');
    setMessage('');
    try {
      const data = await getCase(caseId);
      setSelectedCase(data.case);
      setView('detail');
      setCommentBody('');
    } catch (err) {
      setError(err.error || 'Unable to open case');
    }
  }, []);

  useEffect(() => {
    getCaseMeta()
      .then((data) => setMeta(data))
      .catch(() => {});
    getUsers()
      .then((data) => setUsers(data.users || []))
      .catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    if (view === 'inbox') {
      loadInbox();
    }
  }, [view, loadInbox]);

  useEffect(() => {
    if (initialCaseId) {
      openCase(initialCaseId);
    } else if (initialPrefill) {
      setView('new');
      setForm({
        ...emptyForm,
        ownerId: user?.id || '',
        ...initialPrefill,
      });
    }
  }, [initialCaseId, initialPrefill, openCase, user?.id]);

  const ownerName = useMemo(() => {
    if (!selectedCase?.ownerId) return 'Unassigned';
    const match = users.find((entry) => entry.id === selectedCase.ownerId);
    return match ? `${match.name} (${match.id})` : selectedCase.ownerId;
  }, [selectedCase, users]);

  const goBackToSeasonPass = () => {
    if (!returnToSeasonPassId) return;
    onNavigate('season-pass-detail', {
      seasonPassId: returnToSeasonPassId,
      returnTo: 'search',
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const data = await createCase({
        ...form,
        createdBy: user?.id || form.ownerId,
        ownerId: form.ownerId || user?.id || '',
      });
      setMessage(`Case ${data.case.caseNumber} created`);
      setForm({ ...emptyForm, ownerId: user?.id || '' });
      setSelectedCase(data.case);
      if (returnToSeasonPassId) {
        goBackToSeasonPass();
      } else {
        setView('detail');
      }
    } catch (err) {
      setError(err.error || 'Unable to create case');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateField = async (patch) => {
    if (!selectedCase) return;
    setSaving(true);
    setError('');
    try {
      const data = await updateCase(selectedCase.id, {
        ...patch,
        updatedBy: user?.id,
        updatedByName: user?.name || 'Staff',
      });
      setSelectedCase(data.case);
      setMessage('Case updated');
    } catch (err) {
      setError(err.error || 'Unable to update case');
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!selectedCase) return;
    setSaving(true);
    setError('');
    try {
      const data = await addCaseComment(selectedCase.id, {
        body: commentBody,
        visibility: commentVisibility,
        authorId: user?.id || '',
        authorName: user?.name || 'Staff',
      });
      setSelectedCase(data.case);
      setCommentBody('');
      setMessage('Update posted');
      if (returnToSeasonPassId) {
        goBackToSeasonPass();
      }
    } catch (err) {
      setError(err.error || 'Unable to post update');
    } finally {
      setSaving(false);
    }
  };

  const formatWhen = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  return (
    <div className="container wide-container cases-page">
      <AppHeader
        user={user}
        pageTitle="Cases"
        subtitle="Guest questions & service cases"
        onNavigate={onNavigate}
        onLogout={onLogout}
        active="cases"
      />

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}

      <div className="cases-toolbar">
        <button
          type="button"
          className={view === 'inbox' ? 'btn-primary' : 'btn-secondary'}
          onClick={() => {
            setView('inbox');
            setSelectedCase(null);
            setMessage('');
          }}
        >
          Inbox
        </button>
        <button
          type="button"
          className={view === 'new' ? 'btn-primary' : 'btn-secondary'}
          onClick={() => {
            setView('new');
            setMessage('');
            setForm({ ...emptyForm, ownerId: user?.id || '' });
          }}
        >
          + New Case
        </button>
        {selectedCase && (
          <button
            type="button"
            className={view === 'detail' ? 'btn-primary' : 'btn-secondary'}
            onClick={() => setView('detail')}
          >
            Case {selectedCase.caseNumber}
          </button>
        )}
        {returnToSeasonPassId && (
          <button type="button" className="btn-secondary" onClick={goBackToSeasonPass}>
            ← Back to season pass
          </button>
        )}
      </div>

      {view === 'inbox' && (
        <div>
          <form
            className="cases-filters"
            onSubmit={(e) => {
              e.preventDefault();
              loadInbox();
            }}
          >
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guest, email, case #, ticket…"
            />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="Open">Open cases</option>
              <option value="All">All statuses</option>
              {(meta.statuses || []).map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <button type="submit">Search</button>
          </form>

          {loading ? (
            <p className="subtitle">Loading cases…</p>
          ) : cases.length === 0 ? (
            <p className="subtitle">No cases found. Create one to start guest communication.</p>
          ) : (
            <div className="table-scroll">
              <table className="users-table cases-table">
                <thead>
                  <tr>
                    <th>Case #</th>
                    <th>Subject</th>
                    <th>Guest</th>
                    <th>Status</th>
                    <th>Priority</th>
                    <th>Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {cases.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.caseNumber}</td>
                      <td>{entry.subject}</td>
                      <td>
                        <div>{entry.guestName}</div>
                        <div className="muted-small">{entry.guestEmail}</div>
                      </td>
                      <td>
                        <span className={`case-status status-${String(entry.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                          {entry.status}
                        </span>
                      </td>
                      <td>{entry.priority}</td>
                      <td>{formatWhen(entry.updatedAt)}</td>
                      <td>
                        <button type="button" className="btn-table" onClick={() => openCase(entry.id)}>
                          Open
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {view === 'new' && (
        <form onSubmit={handleCreate} className="case-form">
          <h2>New Case</h2>
          <p className="subtitle">Capture the guest question and open a tracked service case.</p>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="case-guest-name">Guest name *</label>
              <input
                id="case-guest-name"
                value={form.guestName}
                onChange={(e) => setForm((prev) => ({ ...prev, guestName: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="case-guest-email">Guest email</label>
              <input
                id="case-guest-email"
                type="email"
                value={form.guestEmail}
                onChange={(e) => setForm((prev) => ({ ...prev, guestEmail: e.target.value }))}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="case-guest-phone">Guest phone</label>
              <input
                id="case-guest-phone"
                value={form.guestPhone}
                onChange={(e) => setForm((prev) => ({ ...prev, guestPhone: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label htmlFor="case-origin">Origin</label>
              <select
                id="case-origin"
                value={form.origin}
                onChange={(e) => setForm((prev) => ({ ...prev, origin: e.target.value }))}
              >
                {(meta.origins || []).map((origin) => (
                  <option key={origin} value={origin}>
                    {origin}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="case-ticket-type">Ticket type</label>
              <select
                id="case-ticket-type"
                value={form.ticketType}
                onChange={(e) => setForm((prev) => ({ ...prev, ticketType: e.target.value }))}
              >
                <option value="">—</option>
                <option value="Six Flags">Six Flags</option>
                <option value="Enchanted Parks">Enchanted Parks</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="case-ticket-id">Ticket / Pass ID</label>
              <input
                id="case-ticket-id"
                value={form.ticketId}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    ticketId: e.target.value,
                    seasonPassId: prev.seasonPassId || e.target.value,
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label htmlFor="case-season-pass-id">Season Pass ID</label>
              <input
                id="case-season-pass-id"
                value={form.seasonPassId}
                onChange={(e) => setForm((prev) => ({ ...prev, seasonPassId: e.target.value }))}
                placeholder="SFSP-....."
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="case-priority">Priority</label>
              <select
                id="case-priority"
                value={form.priority}
                onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))}
              >
                {(meta.priorities || []).map((priority) => (
                  <option key={priority} value={priority}>
                    {priority}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="case-owner">Owner</label>
              <select
                id="case-owner"
                value={form.ownerId}
                onChange={(e) => setForm((prev) => ({ ...prev, ownerId: e.target.value }))}
              >
                <option value={user?.id || ''}>{user?.name || 'Me'} (me)</option>
                {users
                  .filter((entry) => entry.id !== user?.id)
                  .map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name} ({entry.id})
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="case-subject">Subject</label>
            <input
              id="case-subject"
              value={form.subject}
              onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
              placeholder="Short summary (optional — auto-filled from description)"
            />
          </div>
          <div className="form-group">
            <label htmlFor="case-description">Guest question / description *</label>
            <textarea
              id="case-description"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              required
            />
          </div>
          <button type="submit" disabled={saving}>
            {saving ? 'Creating…' : 'Create Case'}
          </button>
        </form>
      )}

      {view === 'detail' && selectedCase && (
        <div className="case-detail">
          <div className="case-detail-header">
            <div>
              <h2>
                {selectedCase.caseNumber}
                <span className={`case-status status-${String(selectedCase.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                  {selectedCase.status}
                </span>
              </h2>
              <p className="subtitle" style={{ marginBottom: 8 }}>
                {selectedCase.subject}
              </p>
              <p className="muted-small">
                Opened {formatWhen(selectedCase.createdAt)} · Updated {formatWhen(selectedCase.updatedAt)}
                {selectedCase.closedAt ? ` · Closed ${formatWhen(selectedCase.closedAt)}` : ''}
              </p>
            </div>
          </div>

          <div className="case-detail-grid">
            <section className="case-panel">
              <h3>Guest</h3>
              <p>
                <strong>{selectedCase.guestName}</strong>
              </p>
              <p>{selectedCase.guestEmail || '—'}</p>
              <p>{selectedCase.guestPhone || '—'}</p>
              <p className="muted-small">
                {selectedCase.ticketType || 'Ticket'} {selectedCase.ticketId || ''}
              </p>
              {(returnToSeasonPassId ||
                selectedCase.seasonPassId ||
                selectedCase.ticketId) && (
                <button
                  type="button"
                  className="btn-table"
                  style={{ marginTop: 8 }}
                  onClick={() =>
                    onNavigate('season-pass-detail', {
                      seasonPassId:
                        returnToSeasonPassId ||
                        selectedCase.seasonPassId ||
                        selectedCase.ticketId,
                      returnTo: 'search',
                    })
                  }
                >
                  Open Season Pass
                </button>
              )}
              <h3 style={{ marginTop: 20 }}>Case details</h3>
              <p>{selectedCase.description || '—'}</p>
              {selectedCase.resolution && (
                <>
                  <h3 style={{ marginTop: 20 }}>Resolution</h3>
                  <p>{selectedCase.resolution}</p>
                </>
              )}
            </section>

            <section className="case-panel">
              <h3>Handle case</h3>
              <div className="form-group">
                <label htmlFor="detail-status">Status</label>
                <select
                  id="detail-status"
                  value={selectedCase.status}
                  disabled={saving}
                  onChange={(e) => handleUpdateField({ status: e.target.value })}
                >
                  {(meta.statuses || []).map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="detail-priority">Priority</label>
                <select
                  id="detail-priority"
                  value={selectedCase.priority}
                  disabled={saving}
                  onChange={(e) => handleUpdateField({ priority: e.target.value })}
                >
                  {(meta.priorities || []).map((priority) => (
                    <option key={priority} value={priority}>
                      {priority}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="detail-owner">Owner</label>
                <select
                  id="detail-owner"
                  value={selectedCase.ownerId || ''}
                  disabled={saving}
                  onChange={(e) => handleUpdateField({ ownerId: e.target.value })}
                >
                  <option value="">Unassigned</option>
                  {users.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name} ({entry.id})
                    </option>
                  ))}
                </select>
                <p className="muted-small">Current owner: {ownerName}</p>
              </div>
              <div className="form-group">
                <label htmlFor="detail-resolution">Resolution notes</label>
                <textarea
                  id="detail-resolution"
                  value={selectedCase.resolution || ''}
                  onChange={(e) =>
                    setSelectedCase((prev) => ({ ...prev, resolution: e.target.value }))
                  }
                  onBlur={(e) => handleUpdateField({ resolution: e.target.value })}
                />
              </div>
            </section>
          </div>

          <section className="case-panel case-feed">
            <h3>Case feed</h3>
            <p className="subtitle">
              Post public guest updates or internal notes — like Salesforce case comments.
            </p>
            <div className="case-comments">
              {(selectedCase.comments || []).length === 0 ? (
                <p className="subtitle">No updates yet.</p>
              ) : (
                (selectedCase.comments || []).map((comment) => (
                  <article
                    key={comment.id}
                    className={`case-comment ${comment.visibility === 'Public' ? 'public' : 'internal'}`}
                  >
                    <header>
                      <strong>{comment.authorName || comment.authorId || 'Staff'}</strong>
                      <span className="case-comment-meta">
                        {comment.visibility} · {formatWhen(comment.createdAt)}
                      </span>
                    </header>
                    <p>{comment.body}</p>
                  </article>
                ))
              )}
            </div>
            <form onSubmit={handleAddComment} className="case-comment-form">
              <div className="form-group">
                <label htmlFor="case-comment">Add update</label>
                <textarea
                  id="case-comment"
                  value={commentBody}
                  onChange={(e) => setCommentBody(e.target.value)}
                  placeholder="Reply to the guest or leave an internal note…"
                  required
                />
              </div>
              <div className="cases-comment-actions">
                <select
                  value={commentVisibility}
                  onChange={(e) => setCommentVisibility(e.target.value)}
                  aria-label="Comment visibility"
                >
                  <option value="Public">Public (guest-facing)</option>
                  <option value="Internal">Internal only</option>
                </select>
                <button type="submit" disabled={saving || !commentBody.trim()}>
                  Post update
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}

export default Cases;
